import React, { useState } from 'react';
import { Badge, Table } from 'react-bootstrap';
import { EmptyBlock, LoadingBlock, formatDate, formatNumber } from '../central-pharmacy/ui';

export const ALERT_TYPE_LABEL = {
  OUT_OF_STOCK: 'Rupture',
  LOW_STOCK: 'Stock bas',
  EXPIRED: 'Périmé',
  EXPIRING: 'Péremption proche'
};

function alertBadgeBg(alert) {
  if (alert.severity === 'CRITICAL' || alert.type === 'OUT_OF_STOCK' || alert.type === 'EXPIRED') {
    return 'danger';
  }
  return 'warning';
}

function alertDetail(alert) {
  if (alert.type === 'LOW_STOCK' || alert.type === 'OUT_OF_STOCK') {
    return `Dispo ${formatNumber(alert.on_hand)} / seuil ${formatNumber(alert.min_stock)}`;
  }
  return `Lot ${alert.lot_number || '—'} · ${formatDate(alert.expiry_date)} (${alert.days_left ?? '—'} j)`;
}

export default function LocalPharmacyAlertsPanel({ data, loading, compact = false, onViewAll }) {
  const [typeFilter, setTypeFilter] = useState('');

  if (loading) {
    return <LoadingBlock label="Chargement des alertes…" />;
  }

  if (!data) {
    return <EmptyBlock label="Alertes indisponibles." />;
  }

  const summary = data.summary || {};
  const total = Number(summary.total || 0);
  const alerts = (data.alerts || []).filter((a) => !typeFilter || a.type === typeFilter);

  if (!compact && total === 0 && !typeFilter) {
    return (
      <div className="lp-alerts-ok">
        <div className="lp-alerts-ok__icon" aria-hidden>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <p className="lp-alerts-ok__title">Aucune alerte sur ce site</p>
        <p className="lp-alerts-ok__desc">Stock et lots conformes aux seuils configurés.</p>
      </div>
    );
  }

  if (compact) {
    if (total === 0) return null;
    const preview = (data.alerts || []).slice(0, 5);
    return (
      <div className="lp-alerts-banner" role="alert">
        <div className="lp-alerts-banner__head">
          <div className="lp-alerts-banner__title">
            <span className="lp-alerts-banner__icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </span>
            <strong>{formatNumber(total)} alerte{total > 1 ? 's' : ''}</strong> sur ce site
          </div>
          {typeof onViewAll === 'function' ? (
            <button type="button" className="lp-alerts-banner__link" onClick={onViewAll}>
              Voir tout
            </button>
          ) : null}
        </div>
        <ul className="lp-alerts-banner__list">
          {preview.map((a, idx) => (
            <li key={`${a.type}-${a.medication_id}-${idx}`}>
              <Badge bg={alertBadgeBg(a)} className="me-2">
                {ALERT_TYPE_LABEL[a.type] || a.type}
              </Badge>
              <span>{a.medication_name}</span>
              <span className="lp-alerts-banner__detail">{alertDetail(a)}</span>
            </li>
          ))}
        </ul>
        {total > preview.length ? (
          <p className="lp-alerts-banner__more small mb-0">
            + {formatNumber(total - preview.length)} autre{total - preview.length > 1 ? 's' : ''}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <div className="lp-alerts-summary">
        <div className="lp-alerts-summary__item">
          <span className="lp-alerts-summary__value">{formatNumber(summary.total)}</span>
          <span className="lp-alerts-summary__label">Total</span>
        </div>
        <div className="lp-alerts-summary__item lp-alerts-summary__item--danger">
          <span className="lp-alerts-summary__value">{formatNumber(summary.out_of_stock)}</span>
          <span className="lp-alerts-summary__label">Ruptures</span>
        </div>
        <div className="lp-alerts-summary__item lp-alerts-summary__item--warn">
          <span className="lp-alerts-summary__value">{formatNumber(summary.low_stock)}</span>
          <span className="lp-alerts-summary__label">Stocks bas</span>
        </div>
        <div className="lp-alerts-summary__item lp-alerts-summary__item--danger">
          <span className="lp-alerts-summary__value">{formatNumber(summary.expired)}</span>
          <span className="lp-alerts-summary__label">Périmés</span>
        </div>
        <div className="lp-alerts-summary__item lp-alerts-summary__item--warn">
          <span className="lp-alerts-summary__value">{formatNumber(summary.expiring)}</span>
          <span className="lp-alerts-summary__label">Péremption proche</span>
        </div>
      </div>

      <div className="cp-toolbar">
        {['', 'OUT_OF_STOCK', 'LOW_STOCK', 'EXPIRED', 'EXPIRING'].map((t) => (
          <button
            key={t || 'all'}
            type="button"
            className={`cp-filter-chip ${typeFilter === t ? 'is-active' : ''}`}
            onClick={() => setTypeFilter(t)}
          >
            {t === '' ? 'Toutes' : ALERT_TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      {alerts.length === 0 ? (
        <EmptyBlock label="Aucune alerte pour ce filtre." />
      ) : (
        <Table responsive hover className="cp-table mb-0">
          <thead>
            <tr>
              <th>Type</th>
              <th>Médicament</th>
              <th>Détail</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a, idx) => (
              <tr key={`${a.type}-${a.medication_id}-${a.lot_number || ''}-${idx}`}>
                <td>
                  <Badge bg={alertBadgeBg(a)}>{ALERT_TYPE_LABEL[a.type] || a.type}</Badge>
                </td>
                <td>{a.medication_name}</td>
                <td className="small text-muted">{alertDetail(a)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}
